import moment from "moment-timezone";
import { call, put, race, select, take, takeEvery, delay, all } from "redux-saga/effects";
import { defineMessages } from "react-intl";
import * as TaskActions from "./taskActions";
import * as BillingActions from "../billing/billingActions";
import * as AttachmentsActions from "../attachments/attachmentsActions";
import * as AssignmentActions from "../assignment/assignmentActions";
import * as Helpers from "../helpers";
import { initialState } from "../initialState";
import { getTechnicianInfo, makeApiCall, getUserSelectedContract, getUserSelectedStatus, getCreationDateObject } from "../helpers";
import taskServices from "./../../services/taskServices";
import { ALERT_TYPES, showAlert } from "../alert/alertActions";
import { REQUEST_NEW_DUE_DATE_FAILED, REQUEST_NEW_DUE_DATE_SUCCEEDED, requestNewDueDate } from "../../redux/requestDueDate/requestNewDueDateActions";
import { OTHER_SUBREASON_CODES, REASON_CODES } from "../../globals/rapReasonCodes";
import { STATUSES, WORKORDER_STATUS_MAP } from "../../globals/workOrderStatus";
import Cookies from "universal-cookie";
import { SAVE_FOLLOW_UPS_INFO_FAILED, SAVE_FOLLOW_UPS_INFO_SUCCEEDED, saveFollowUps } from "../followUps/followUpsActions";

export const BATCH_SIZE = 100;

const messages = defineMessages({
    taskActionSucceeded: {
        id: "alerts.task-sagas.task-action-succeeded",
        defaultMessage: "{actionDesc} succeeded",
        description: "Task action succeeded alert message",
    },
    taskActionFailed: {
        id: "alerts.task-sagas.task-action-failed",
        defaultMessage: "{actionDesc} failed {responseError}",
        description: "Task action failed alert message",
    },
    taskSaveSucceeded: {
        id: "alerts.task-sagas.task-save-succeeded",
        defaultMessage: "WorkOrder saved successfully",
        description: "WO save succeeded alert message",
    },
    taskSaveFailed: {
        id: "alerts.task-sagas.task-save-failed",
        defaultMessage: "WO# ${woNumber} failed {responseError}",
        description: "WO save failed alert message",
    },
    taskIncompleteSucceeded: {
        id: "alerts.task-sagas.task-incomplete-succeeded",
        defaultMessage: "WO# {woNumber} incompleted",
        description: "WO incomplete succeeded alert message",
    },
    taskIncompleteFailed: {
        id: "alerts.task-sagas.task-incomplete-failed",
        defaultMessage: "WO# {woNumber} incomplete failed {responseError}",
        description: "WO incomplete failed alert message",
    },
    taskIncompleteFailedRefresh: {
        id: "alerts.task-sagas.task-incomplete-failed-refresh",
        defaultMessage: "WO# {woNumber} Incomplete timeout received, please refresh",
        description: "WO incomplete failed alert message, please retry",
    },
    taskUnassign: {
        id: "alerts.task-sagas.task-unassign",
        defaultMessage: "Unassign",
        description: "WO status unassign",
    },
    taskEnRoute: {
        id: "alerts.task-sagas.task-en-route",
        defaultMessage: "En Route",
        description: "WO status en route",
    },
    taskEnRouteOnsideFailed: {
        id: "alerts.task-sagas.task-en-route-onsite-failed",
        defaultMessage: "You already have a work order in status Enroute or Onsite.",
        description: "Error when trying to set another as Enroute or Onsite",
    },
    taskOnSite: {
        id: "alerts.task-sagas.task-on-site",
        defaultMessage: "On Site",
        description: "WO status on site",
    },
    taskComplete: {
        id: "alerts.task-sagas.task-complete",
        defaultMessage: "Complete",
        description: "WO status complete",
    },
    taskBillingApproval: {
        id: "alerts.task-sagas.billing-approval",
        defaultMessage: "Bell Billing Approval",
        description: "WO status Bell Billing Approval",
    },
    taskFolloUpSucceeded: {
        id: "alerts.task-sagas.task-followup-succeeded",
        defaultMessage: "WO# {woNumber} followups are updated.",
        description: "WO followup succeeded alert message",
    },
    statusClickFailed: {
        id: "alerts.task-sagas.status-click-failed",
        defaultMessage: "{responseError}",
        description: "Status Update failed in CLICK",
    },
    taskFolloUpFailed: {
        id: "alerts.task-sagas.task-followUp-failed",
        defaultMessage: "WO# {woNumber} followUp failed {responseError}",
        description: "WO followUp failed alert message",
    },
    savePermitsSucceeded: {
        id: "alerts.task-sagas.save-permit-succeeded",
        defaultMessage: "Save Permits Succeeded",
        description: "WO Save Permits Succeeded",
    },
    savePermitsFailed: {
        id: "alerts.task-sagas.save-permit-failed",
        defaultMessage: "Save Permits Failed",
        description: "WO Save Permits Failed",
    },
    savePermitsSucceeded: {
        id: "alerts.task-sagas.save-permit-succeeded",
        defaultMessage: "Save Permits Succeeded",
        description: "WO Save Permits Succeeded",
    },
    savePermitsFailed: {
        id: "alerts.task-sagas.save-permit-failed",
        defaultMessage: "Save Permits Failed",
        description: "WO Save Permits Failed",
    },
});

export const watchTaskRequests = function* () {
    yield takeEvery(TaskActions.REQUEST_TASKS, fetchTasksAsync);
    yield takeEvery(TaskActions.REQUEST_TASKS_FOR_TECH, fetchTasksForTechnicianAsync);
    yield takeEvery(TaskActions.REQUEST_TASK_RELOAD, fetchTaskAsync);
    yield takeEvery(TaskActions.REQUEST_ALL_STATUS, fetchAllStatusAsync);

    yield takeEvery(TaskActions.TASK_UNASSIGN, taskUnassign);
    yield takeEvery(TaskActions.TASK_ENROUTE, taskEnroute);
    yield takeEvery(TaskActions.TASK_ONSITE, taskOnsite);
    yield takeEvery(TaskActions.REQUEST_SENT_TO_BELL, sentToBellAsync);
    yield takeEvery(TaskActions.TASK_COMPLETE, taskComplete);

    yield takeEvery(TaskActions.REQUEST_WORKORDER, fetchWorkorderAsync);
    yield takeEvery(TaskActions.REQUEST_WORKORDER_PURCHASE_NUM, fetchWorkorderPONum);
    yield takeEvery(TaskActions.REQUEST_WORKORDER_VENDOR_MANAGER, fetchVendorManager);
    yield takeEvery(TaskActions.REQUEST_TASK_PATCH, taskPatch);

    yield takeEvery(TaskActions.INCOMPLETE_TASK_WITH_REASON, incompleteTaskWithReasonAsync);

    yield takeEvery(TaskActions.SAVE_PERMIT_INFO, savePermitInfo);
    yield takeEvery(TaskActions.GET_TASK_RELATEDWO, getRelatedWO);
    yield takeEvery(TaskActions.GET_TASK_ASSIGNMENT, getAssignmentInformation);
    yield takeEvery(TaskActions.GET_WORKORDER_CANCELLED_REASON, getCancelWorkorderReason);
};

export const fetchTasksAsync = function* (action) {
    const cookies = new Cookies();
    const lang = cookies.get("locale");
    try {
        const { fetchCounts, workOrderNumber } = action;
        const pageSize = parseInt(process.env.REACT_APP_FETCH_TASKS_PAGE_SIZE) || 500;

        const technicianInfo = yield select(getTechnicianInfo);
        const contractNumber = yield select(getUserSelectedContract);
        const getStatuses = yield select(getUserSelectedStatus);
        const creationDateObject = yield select(getCreationDateObject);

        // Always fetch counts — needed to calculate page count for pagination
        const taskCounts = yield call(
            makeApiCall,
            taskServices.getTaskCounts,
            technicianInfo.vendorNumber,
            contractNumber,
            getStatuses,
            creationDateObject,
            workOrderNumber,
        );

        const totalCount = taskCounts?.totalCount ?? 0;
        const pageCount = Math.max(1, Math.ceil(totalCount / pageSize));

        const pageRequests = Array.from({ length: pageCount }, (_, i) =>
            call(
                makeApiCall,
                taskServices.getTasks,
                technicianInfo.vendorNumber,
                contractNumber,
                getStatuses,
                creationDateObject,
                workOrderNumber,
                i,
                pageSize,
            ),
        );

        const allPageResults = yield all(pageRequests);

        const fetchedTasks = filterTaskData(
            allPageResults.filter((pageResult) => pageResult != null).flat(),
        );

        const tasks = {
            fetching: false,
            tasks: fetchedTasks,
            counts: taskCounts,
        };
        yield put(TaskActions.requestTasksSucceeded(tasks, lang));
        yield put(AssignmentActions.requestAssignmentsSucceeded(filterTaskData(fetchedTasks, lang)));
    } catch (responseError) {
        yield put(TaskActions.requestTasksFailed());
        yield put(AttachmentsActions.fetchAttachmentsFailed());
    }
};

export const fetchTasksForTechnicianAsync = function* () {
    try {
        const technicianInfo = yield select(getTechnicianInfo) || {};
        const taskData = yield makeApiCall(taskServices.getTasksForTechnician, technicianInfo.id);
        let taskValues = filterLabourerTaskData(taskData);

        const tasks = {
            fetching: false,
            tasks: taskValues,
            counts: 1,
        };

        yield put(TaskActions.requestTasksSucceeded(tasks));
    } catch (responseError) {
        yield put(TaskActions.requestTasksFailed());
        yield put(AttachmentsActions.fetchAttachmentsFailed());
    }
};

export const fetchTaskAsync = function* (action) {
    let technicianInfo = yield select(getTechnicianInfo);
    if (!technicianInfo) {
        yield delay(100);
        technicianInfo = yield select(getTechnicianInfo);
    }
    try {
        const workorderDataResp = yield makeApiCall(taskServices.getIndWorkOrder, action.workorderNumber, technicianInfo.vendorNumber, false, false);

        if (workorderDataResp && workorderDataResp.length > 0) {
            const workOrder = mapTask(workorderDataResp[0]);

            if (action.isUnassigned) {
                yield put(TaskActions.fetchWorkorder(action.workorderNumber));
            } else {
                // refresh task list state
                yield handleTaskReload(workOrder);
            }
        }
    } catch (responseError) {
        yield put(TaskActions.requestTasksFailed());
        yield put(AttachmentsActions.fetchAttachmentsFailed());
    }
};

const parseDate = (input) => {
    return input && typeof input === "string" ? moment.utc(Date.parse(input?.slice(0, 19))) : input;
};

const mapTask = (task) => {
    task.id = task?.workOrderNumber;
    task["address"] = task?.civicNumber + " " + task?.streetName;
    task["tab"] = ""; // Visual element
    task.creationDate = parseDate(task?.creationDate);
    task.dueDate = parseDate(task?.dueDate);
    task["resourceAssigned"] = {
        id: task?.resourceAssignedTo,
        firstName: task?.resourceAssignedTo_firstName,
        lastName: task?.resourceAssignedTo_lastName,
    };
    task["workType"] = {
        name_en: task?.worktypeName_en,
        name_fr: task?.worktypeName_fr,
    };
    task["activties"] = {
        name_en:
            task["workActivityId"] === 296
                ? "Buried Service Wire"
                : task["workActivityId"] === 299
                ? "Place Cable - Buried (Bell cable tech use only)"
                : task?.workActivityName_en,
        name_fr:
            task["workActivityId"] === 296
                ? "Fils de Service Enfoui"
                : task["workActivityId"] === 299
                ? "Placer le câble - Enfouis (Réservé aux techniciens des câbles Bell)"
                : task?.workActivityName_fr,
    };
    task["Was_Temp_Placed"] = {
        name_en: task?.noTempName_en,
        name_fr: task?.noTempName_fr,
    };
    if (task?.statusId === STATUSES.BILLING_APPROVED || task?.statusId === STATUSES.ERROR_PO_CREATION || task?.statusId === STATUSES.PENDING_PO) {
        task["status"] = {
            id: STATUSES.BILLING_APPROVED,
            name_en: "Bell Billing Approval",
            name_fr: "Approbation de facturation Bell",
        };
    } else {
        task["status"] = {
            id: task?.statusId,
            name_en: task?.statusName_en,
            name_fr: task?.statusName_fr,
        };
    }
    task["additionalAction_Display"] = {
        name_en: task?.additionalAction_EN,
        name_fr: task?.additionalAction_FR,
    };
    task["reasonCode"] = {
        name_en: task?.reasonCodeName_en,
        name_fr: task?.reasonCodeName_fr,
    };
    task["priority"] = {
        name_en: task?.priorityName_en.split(" ")[0],
        name_fr: task?.priorityName_fr.split(" ")[0],
    };
    task["networkType"] = {
        name_en: task?.networkType_EN,
        name_fr: task?.networkType_FR,
    };
    task["permitStatus"] = {
        name_en: task?.permitStatusEn,
        name_fr: task?.permitStatusFr,
    };
    task["followUpType"] = {
        name_en: task?.followUpType_EN,
        name_fr: task?.followUpType_Fr,
    };
    task["plantType"] = {
        name_en: task?.plantTypeEn,
        name_fr: task?.plantTypeFr,
    };
    task["parentJobClassification"] = {
        name_en: task?.parentType_EN,
        name_fr: task?.parentType_FR,
    };
    task["subReasonCode"] = {
        id: task?.subReasonCodeId,
        name_en: task?.subReasonCode_EN,
        name_fr: task?.subReasonCode_Fr,
    };
    task["flaggingRequired"] = {
        name_en: task?.flaggingReqName_en,
        name_fr: task?.flaggingReqName_fr,
    };
    task["permitRequired"] = {
        name_en: task?.isPermitReqName_en,
        name_fr: task?.isPermitReqName_fr,
    };
    task["completionCode"] = {
        name_en: task?.completionCodeName_en,
        name_fr: task?.completionCodeName_fr,
    };
    task.scheduledDate = task?.scheduledDate && typeof task?.scheduledDate === "string" ? moment.utc(task?.scheduledDate) : task?.scheduledDate;
    task.completionDate =
        task?.completionDate && typeof task?.completionDate === "string" ? moment(task?.completionDate).tz("America/Toronto") : task?.completionDate;
    return task;
};

const workOrderDataFormatting = (
    workorderDataResp,
    workOrder,
    activityQuestions,
    getAssignmentInformationResponse,
    getMultipleWorkActivityInformationResponse,
    getRelatedWOResponse,
    orderedRelatedWOBodyData,
) => {
    const workOrderDetails = workOrder.workOrderDetail || {};

    // get confined space info from the WO's confinedSpaceNumber
    var confinedSpaceInfo = {};
    confinedSpaceInfo.mHIdentifierId = workOrderDetails.manHoleIdentifierId;
    confinedSpaceInfo.mHIdentifierRoute = workOrderDetails.manHoleIdentifierRoute;
    confinedSpaceInfo.trafficControlRequired = workOrderDetails.trafficControlRequired;
    confinedSpaceInfo.doubleLidded = workOrderDetails.doubleLidded;
    confinedSpaceInfo.notifyTechnician = workOrderDetails.contractorOnSite;
    confinedSpaceInfo.confinedSpaceTypeId = workOrderDetails.cSType;
    confinedSpaceInfo.cSEntryNumber = workOrderDetails.confinedSpaceNumber;
    confinedSpaceInfo.manHoleContaminated = workOrderDetails.manholeContaminated;
    var assignmentInformation;
    if (getAssignmentInformationResponse) {
        assignmentInformation = getAssignmentInformationResponse.body;
    } else {
        assignmentInformation = "";
    }
    var multipleWorkActivityInformation;
    if (getMultipleWorkActivityInformationResponse) {
        multipleWorkActivityInformation = getMultipleWorkActivityInformationResponse;
    } else {
        multipleWorkActivityInformation = "";
    }
    var relatedIdInformation;
    let relatedWOList;
    if (getRelatedWOResponse) {
        relatedIdInformation = getRelatedWOResponse.filter((RelatedData) => RelatedData.workOrderId === workOrder.workOrderNumber);
        relatedWOList = getRelatedWOResponse;
    } else {
        relatedIdInformation = "";
        relatedWOList = "";
    }
    const compWorkOrderDetails = {
        workorderData: workOrder,
        workorderInfo: workOrderDetails,
        assignmentInformation: assignmentInformation,
        relatedIdInformation: relatedIdInformation[0],
        multipleWorkActivityInformation: multipleWorkActivityInformation,
        confinedSpaceInfo: confinedSpaceInfo || {},
        realtedWOListData: relatedWOList,
        activityQuestions,
        orderedRelatedWOData: orderedRelatedWOBodyData,
    };
    return compWorkOrderDetails;
};

const filterTaskData = (taskData) => {
    var taskValues = [];
    if (taskData) {
        taskValues = taskData.map(mapTask);
    }
    return taskValues;
};

const mapLabourerTask = (task) => {
    task["address"] = task.civicNumber + " " + task.streetName;
    task["tab"] = ""; // Visual element
    task.creationDate = parseDate(task?.creationDate);
    task.dueDate = parseDate(task?.dueDate);
    task.scheduledDate = parseDate(task?.scheduledDate);
    task.completionDate = task.completionDate ? moment(task?.completionDate).tz("America/Toronto") : task.completionDate;
    if (task.FK_workOrderNumber) task.workOrderNumber = task.FK_workOrderNumber;

    if (task.FK_status === STATUSES.PENDING_PO || task.FK_status === STATUSES.ERROR_PO_CREATION) {
        task.status.id = STATUSES.BILLING_APPROVED;
        task.status.name_en = "Billing Approved";
        task.status.name_fr = "Facturation approuvée";
    }

    task.latestFollowUpDate = "";
    task.followUpType = "";
    task.referenceNumber = "";
    if (task?.workOrderDetail?.followUps && task?.workOrderDetail?.followUps?.results && task?.workOrderDetail?.followUps?.results?.length) {
        const followups = task.workOrderDetail.followUps.results;
        let latestFollowUp = {};
        task.workOrderDetail.followUps.results = followups.map((followUp) => {
            followUp.creationDate = new Date(new Date(followUp.creationDate).getTime() + 5 * 60 * 60 * 1000).toISOString();
            latestFollowUp =
                latestFollowUp.creationDate && moment(latestFollowUp.creationDate).isAfter(followUp.creationDate) ? latestFollowUp : followUp;
            followUp.dueDate = followUp.dueDate ? new Date(new Date(followUp.dueDate).getTime() + 5 * 60 * 60 * 1000).toISOString() : null;
            return followUp;
        });

        task.latestFollowUpDate = latestFollowUp.creationDate;
        task.followUpType = latestFollowUp.followUpTpyeId;
        task.referenceNumber = latestFollowUp.referenceTicketNumber || "";
    }
    return task;
};

const filterLabourerTaskData = (taskData) => {
    var taskValues = [];
    if (taskData) {
        taskValues = taskData.map(mapLabourerTask);
    }
    return taskValues;
};

const fetchWorkorderAsync = function* (action) {
    const { workorderNumber, withSKUs } = action;
    const keycloak = yield select(Helpers.getKeycloak);
    const isLabourer = keycloak?.hasRealmRole("ContractorLabourerRole");
    let technicianInfo = yield select(getTechnicianInfo);
    if (!technicianInfo) {
        yield delay(5000);
        technicianInfo = yield select(getTechnicianInfo);
    }
    try {
        let workorderDataResp = yield makeApiCall(
            taskServices.getIndWorkOrder,
            workorderNumber,
            technicianInfo.vendorNumber,
            withSKUs,
            true,
            isLabourer,
        );
        if (workorderDataResp) {
            // Sometimes the result is empty because of something with the backend, so try one more time
            if (workorderDataResp.length === 0) {
                workorderDataResp = yield makeApiCall(
                    taskServices.getIndWorkOrder,
                    workorderNumber,
                    technicianInfo.vendorNumber,
                    withSKUs,
                    true,
                    isLabourer,
                );
            }
            if (workorderDataResp && workorderDataResp.length > 0) {
                let workOrder;
                let activityId;
                let relatedID;
                if (keycloak?.hasRealmRole("ContractorLabourerRole")) {
                    workOrder = mapLabourerTask(workorderDataResp[0]);
                    activityId =
                        workOrder["workOrderActivity"]?.inputRequestActivityId != null
                            ? workOrder?.workOrderActivity?.inputRequestActivityId
                            : workOrder?.FK_workActivity;
                    relatedID = workOrder?.workOrderDetail?.relatedId;
                } else {
                    workOrder = mapTask(workorderDataResp[0]);
                    activityId =
                        workOrder["workOrderActivity"]?.inputRequestActivityId != null
                            ? workOrder?.workOrderActivity?.inputRequestActivityId
                            : workOrder.workActivity;
                    relatedID = workOrder?.originalRelatedId;
                }

                const activityQuestionsResp = yield makeApiCall(taskServices.getActivityQuestions, activityId);
                const activityQuestions = activityQuestionsResp.sort((a, b) => a.displayOrder - b.displayOrder);

                const activitiesRes = yield makeApiCall(taskServices.getMultipleWorkActivityInformation, workOrder.workOrderNumber);

                const orderedRelatedWOData = yield makeApiCall(taskServices.getOrderedRelatedData, relatedID, workorderNumber);
                const getRelatedWOResponse = yield makeApiCall(taskServices.getRelatedWO, relatedID);
                if (getRelatedWOResponse) {
                }
                const compWorkOrderDetails = workOrderDataFormatting(
                    workorderDataResp,
                    workOrder,
                    activityQuestions,
                    null,
                    activitiesRes,
                    getRelatedWOResponse,
                    orderedRelatedWOData,
                );
                yield getAllAssignment(
                    compWorkOrderDetails?.relatedIdInformation?.accessId
                        ? compWorkOrderDetails?.relatedIdInformation?.accessId
                        : compWorkOrderDetails?.relatedIdInformation?.customerPhoneNumber,
                );
                yield put(TaskActions.fetchWorkorderSuccess(compWorkOrderDetails));

                // refresh task list state
                yield handleTaskReload(workOrder);
                if (action?.isTechnician) {
                    yield call(action?.history?.push, "/");
                }
            }
        }
    } catch (responseError) {
        yield put(TaskActions.requestTasksFailed());
        yield put(AttachmentsActions.fetchAttachmentsFailed());
    }
};

let accessID = [];

const getAllAssignment = function* (customerPhoneNumber) {
    const arrayOfAccessId = !!customerPhoneNumber ? customerPhoneNumber?.split(" | ") : null;
    if (!arrayOfAccessId) {
        yield put(TaskActions.getAssignmentInformationSuccess("No AccessID"));
        return;
    }
    for (const accessId of arrayOfAccessId) {
        const responseData = yield makeApiCall(taskServices.getAssignmentInformation, accessId || "");
        if (customerPhoneNumber?.includes("|")) {
            if (responseData?.activeServiceInventoryInformation || responseData?.pendingServiceInventoryInformation) {
                accessID.push({ ...responseData, accessId });
            }
        } else {
            if (typeof responseData === "string" || !arrayOfAccessId) {
                yield put(TaskActions.getAssignmentInformationSuccess(responseData));
            } else {
                yield put(TaskActions.getAssignmentInformationSuccess([{ ...responseData, accessId }]));
            }
        }
    }
    if (customerPhoneNumber?.includes("|")) {
        yield put(TaskActions.getAssignmentInformationSuccess(accessID));
    }
};
const fetchWorkorderPONum = function* (action) {
    const billingsInfo = yield makeApiCall(taskServices.getBillingsByWO, action.workorderNumber);
    yield put(TaskActions.fetchWorkorderPONumSuccess(billingsInfo[0]));
};

const fetchVendorManager = function* (action) {
    let vendorManagerResponse = yield call(makeApiCall, taskServices.getVendorManager, {
        clliCode: action.clliCode,
        workActivityId: action.workActivityId,
    });
    yield put(TaskActions.fetchVendorManagerSuccess(vendorManagerResponse.body.length > 0 ? vendorManagerResponse.body[0] : null));
};

// Updates the task list and the assignment list with the
// reloaded task info
const handleTaskReload = function* (workOrder) {
    // Update the tasks list state
    yield put(TaskActions.taskReloadSucceeded(workOrder));

    // TODO: update the assignment info
    const assignment = AssignmentActions.assignmentFromTask(workOrder);
    yield put(AssignmentActions.updateAssignment(assignment));
};

export const taskUnassign = function* (action) {
    yield taskAction(action.workOrderNumber, taskServices.actions.unassign, messages.taskUnassign);
};

export const taskEnroute = function* (action) {
    yield call(taskAction, action.workOrderNumber, taskServices.actions.enroute, messages.taskEnRoute, undefined, action.shouldRefresh);
};

export const taskOnsite = function* (action) {
    yield taskAction(action.workOrderNumber, taskServices.actions.onsite, messages.taskOnSite, undefined, action.shouldRefresh);
};

export const taskComplete = function* (action) {
    yield taskAction(action.workOrderNumber, taskServices.actions.complete, messages.taskComplete, false);
};

export const sentToBellAsync = function* (action) {
    yield taskAction(action.workOrderNumber, taskServices.actions.pendingBillingApproval, messages.taskBillingApproval, action.withSKUs);
};

export const taskAction = function* (woNumber, actionName, actionDesc, withSKUs, shouldRefresh) {
    try {
        const technicianInfo = yield select(getTechnicianInfo);
        const actionResult = yield call(makeApiCall, taskServices.taskAction, woNumber, actionName, technicianInfo.userName);
        if (actionResult && actionResult.statusCode && actionResult.statusCode === "OK") {
            if (!shouldRefresh && (actionName === "enroute" || actionName === "onsite")) {
                yield put(TaskActions.updateWorkOrderStatus(WORKORDER_STATUS_MAP[actionName]));
                yield put(TaskActions.requestTaskReload(woNumber, false));
                yield put(showAlert(Date.now(), messages.taskActionSucceeded, ALERT_TYPES.success, { actionDesc }, true));
                return;
            }
            const workOrderNumber = actionName === "completeTask" ? woNumber.workOrderNumber : woNumber;
            yield put(TaskActions.fetchWorkorder(workOrderNumber, withSKUs));

            // Show UI alert to the user so he know that the action succeeded
            yield put(showAlert(Date.now(), messages.taskActionSucceeded, ALERT_TYPES.success, { actionDesc }, true));
        } else if (actionResult && actionResult.statusCode && actionResult.statusCode === "BAD_REQUEST") {
            // Show UI alert to the user so he know that the action succeeded but could not change status
            yield put(showAlert(Date.now(), messages.taskEnRouteOnsideFailed, ALERT_TYPES.error));
        } else if (actionResult && actionResult.statusCodeValue && actionResult.statusCodeValue === 409) {
            // Show UI alert to the user so he know that the action succeeded but could not change status
            let responseError = actionResult.body;
            //yield put(showAlert(Date.now(), actionResult.body, ALERT_TYPES.error));
            yield put(showAlert(Date.now(), messages.statusClickFailed, ALERT_TYPES.error, { responseError }, true));
        }
    } catch (responseError) {
        yield put(showAlert(Date.now(), messages.taskActionFailed, ALERT_TYPES.error, { actionDesc, responseError }, true));
        yield put(TaskActions.requestTasksFailed());
        yield put(AttachmentsActions.fetchAttachmentsFailed());
    }
};
const fetchAllStatusAsync = function* () {
    try {
        const technicianInfo = yield select(getTechnicianInfo);
        const keycloak = yield select(Helpers.getKeycloak);
        const allStatusData = yield makeApiCall(taskServices.getAllStatus, technicianInfo?.vendorNumber, keycloak?.userInfo?.sub);
        yield put(TaskActions.requestAllStatusSucceeded(allStatusData));
    } catch (responseError) {
        yield put(TaskActions.requestAllStatusFailed());
    }
};
const taskPatch = function* (action) {
    const { taskId, workOrderNumber, patchDetails, withSKUs } = action;
    try {
        const patch = yield makeApiCall(taskServices.taskPatch, taskId, patchDetails);
        // if (patch && patch.statusCode && patch.statusCode === "OK") {

        // refresh the updated task
        yield put(TaskActions.fetchWorkorder(workOrderNumber, withSKUs ? true : false));

        // Show UI alert to the user so he know that the action succeeded
        yield put(showAlert(Date.now(), messages.taskSaveSucceeded, ALERT_TYPES.success));
        //}
    } catch (responseError) {
        yield put(showAlert(Date.now(), messages.taskSaveFailed, ALERT_TYPES.error, { workOrderNumber, responseError }));
        yield put(TaskActions.requestTasksFailed());
        yield put(AttachmentsActions.fetchAttachmentsFailed());
    }
};

const incompleteTaskWithReasonAsync = function* (action) {
    const technicianInfo = yield select(getTechnicianInfo);
    action.jobData["userName"] = technicianInfo.userName;
    const { dueDate, newDueDate, workOrderNumber } = action.jobData;
    try {
        // incompletejob
        const patch = yield Helpers.makeApiCallWithParentErrorHandling(taskServices.incompleteTaskWithReason, action.jobData, {
            handleErrorInParent: true,
        });
        // save followups
        if (action.followUpInfo) yield put(saveFollowUps(action.followUpInfo, workOrderNumber));

        if (patch && patch.statusCode && patch.statusCode === "OK") {
            if (dueDate && newDueDate && dueDate < newDueDate) {
                const requestBody = {
                    workOrderNumber: workOrderNumber,
                    reasonCode: REASON_CODES.OTHER,
                    subReasonCode: OTHER_SUBREASON_CODES.CUSTOMER_REQUESTED_NEW_DATE,
                    comments: "",
                    newDueDate: newDueDate,
                };
                // RAP request will change status to unassigned internally
                yield put(requestNewDueDate(requestBody, true));
                yield race([take(REQUEST_NEW_DUE_DATE_SUCCEEDED), take(REQUEST_NEW_DUE_DATE_FAILED)]);
            } else {
                // Store response if no other status change is to be made
                yield put(TaskActions.incompleteTaskResponse(patch));
            }
            // wait untill save followups api completes
            if (action.followUpInfo) yield take([SAVE_FOLLOW_UPS_INFO_SUCCEEDED, SAVE_FOLLOW_UPS_INFO_FAILED]);
            // refresh the updated task
            yield put(BillingActions.fetchBillingComments(workOrderNumber));
            yield put(TaskActions.fetchWorkorder(workOrderNumber, true));
            // Show UI alert to the user so he know that the action succeeded
            yield put(showAlert(Date.now(), messages.taskIncompleteSucceeded, ALERT_TYPES.success, { woNumber: workOrderNumber }));
        } else {
            yield put(
                showAlert(Date.now(), messages.taskIncompleteFailed, ALERT_TYPES.error, {
                    woNumber: workOrderNumber,
                    responseError: patch?.body ?? "Internal Error",
                }),
            );
        }
    } catch (responseError) {
        // request timout at server but incomplete will get succeeded in the backkground.
        if (responseError.response.status === 504) {
            // calling save followups as incomplete call failed
            if (action.followUpInfo) yield put(saveFollowUps(action.followUpInfo, workOrderNumber));
            // wait untill save followups api completes
            if (action.followUpInfo) yield take([SAVE_FOLLOW_UPS_INFO_SUCCEEDED, SAVE_FOLLOW_UPS_INFO_FAILED]);
            yield put(
                showAlert(Date.now(), messages.taskIncompleteFailedRefresh, ALERT_TYPES.error, {
                    woNumber: workOrderNumber,
                }),
            );
        } else {
            yield* Helpers.handleApiErrorGeneric((alert) => put(alert), { message: responseError.err }, null);
        }
        yield put(TaskActions.requestTasksFailed());
        yield put(TaskActions.incompleteTaskResponse(null));
        yield put(AttachmentsActions.fetchAttachmentsFailed());
    }
};

export const savePermitInfo = function* (action) {
    const technicianInfo = yield select(getTechnicianInfo);
    try {
        const permitInfoResponse = yield call(makeApiCall, taskServices.savePermitInfos, action.permitInfo, technicianInfo.userName);
        // refresh the updated task
        if (action.refresh) {
            yield put(TaskActions.fetchWorkorder(action.workOrderNumber, false));
        }
        yield put(showAlert(Date.now(), messages.savePermitsSucceeded, ALERT_TYPES.success));
    } catch (responseError) {
        yield put(showAlert(Date.now(), messages.savePermitsFailed, ALERT_TYPES.error, { responseError: responseError.body }));
    }
};

export const getAssignmentInformation = function* (action) {
    try {
        const getAssignmentInformationResponse = yield call(makeApiCall, taskServices.getAssignmentInformation, action.phoneNumber);
        // refresh the updated task
        if (action.refresh) {
            yield put(TaskActions.getAssignmentInformation(action.phoneNumber, false));
        }
        yield put(TaskActions.getAssignmentInformationSuccess(getAssignmentInformationResponse));
        // yield put(showAlert(Date.now(), messages.savePermitsSucceeded, ALERT_TYPES.success));
    } catch (responseError) {
        yield put(showAlert(Date.now(), messages.savePermitsFailed, ALERT_TYPES.error, { responseError: responseError.body }));
    }
};

export const getRelatedWO = function* (action) {
    try {
        const getRelatedWOResponse = yield call(makeApiCall, taskServices.getRelatedWO, action.relatedId);
        // refresh the updated task
        if (action.refresh) {
            yield put(TaskActions.getRelatedWO(action.relatedId, false));
        }
        yield put(TaskActions.getRelatedWOSuccess(getRelatedWOResponse));
    } catch (responseError) {
        yield put(showAlert(Date.now(), messages.taskActionFailed, ALERT_TYPES.error, { responseError: responseError.body }));
    }
};
export const getCancelWorkorderReason = function* (action) {
    try {
        let technicianInfo = yield select(getTechnicianInfo);
        if (!technicianInfo) {
            yield delay(5000);
            technicianInfo = yield select(getTechnicianInfo);
        }
        const cancelledWorkOrderReasonResp = yield call(
            makeApiCall,
            taskServices.getCancelWorkorderReason,
            action.workOrderNumber,
            technicianInfo.vendorNumber,
        );
        yield put(TaskActions.getCancelWorkorderReasonSuccess(cancelledWorkOrderReasonResp));
    } catch (responseError) {
        yield put(showAlert(Date.now(), messages.taskActionFailed, ALERT_TYPES.error, { responseError: responseError.body }));
    }
};
